import { useCallback, useContext, useEffect, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { GET_EMB_FERT_DATA_BY_DONOR_IDQuery } from '@/rtk-query.gql';
import { PrimaryContext } from '@/services';
import { formatDateWithPattern, formatTime } from '@/utils/formatting';

import { useAspirationFormResources } from '../../hooks/useAspirationFormResources/index.ts';
import { useAutosyncCounts } from '../../hooks/useAutosyncCounts/index.ts';
import { useOpuSearchContext } from '../../hooks/useOpuContext/index.tsx';
import { opuSearchFormDefaultValues } from '../../utils/defaults.ts';
import {
  OpuSearchFormValues,
  useOpuSearchFormSchema,
} from '../../utils/schema.ts';
import { OpuSearchActions } from '../OpuSearchActions/index.tsx';
import { OpuSearchFormFields } from '../OpuSearchFormFields/index.tsx';
import { OpuSearchFormGrids } from '../OpuSearchFormGrids/index.tsx';
import { OpuSearchFormIvmInfo } from '../OpuSearchFormIvmInfo/index.tsx';
import { OpuSearchFormInfo } from '../OpuSearchInfo/index.tsx';

import './index.css';

export type Props = {
  className?: string;
  setShowForm: (v: boolean) => void;
  refetch: () => void;
  poolNumArray: number[];
  originalTimeZone: string | null;
  setOriginalTimeZone: React.Dispatch<React.SetStateAction<string | null>>;
};

export const OpuSearchForm = ({
  setShowForm,
  refetch,
  poolNumArray,
  originalTimeZone,
}: Props) => {
  const { t } = useTranslation();
  const { themeMode } = useContext(PrimaryContext);
  const { selectedAspiration } = useOpuSearchContext();
  const [params, setParams] = useSearchParams();

  const toUrlDate = (d: Date) => formatDateWithPattern(d, 'yyyy-MM-dd');
  const opuDate = params.get('date') ?? toUrlDate(new Date());
  const opuSiteId = params.get('site');
  const tzParam = params.get('tz') ?? '';
  const ivmTechId = params.get('tech') ?? '';

  const {
    aspirationForm,
    aspirationData,
    donorScheduleData,
    embFertDataByDonorId,
    cytoplasmQualityData,
    saveAspiration,
    loading,
    initialLoading,
  } = useAspirationFormResources({ selectedAspiration, opuDate, opuSiteId });

  const opuSearchFormSchema = useOpuSearchFormSchema();
  const {
    control,
    setValue,
    watch,
    reset,
    handleSubmit,
    formState: { dirtyFields },
  } = useForm<OpuSearchFormValues>({
    defaultValues: opuSearchFormDefaultValues,
    resolver: zodResolver(opuSearchFormSchema),
  });

  // ⬅️ Toast ref + pass into hook
  const toastRef = useRef<Toast>(null);
  useAutosyncCounts(watch, setValue, { toast: toastRef });

  const didInitReset = useRef(false);
  useEffect(() => {
    if (!aspirationForm || didInitReset.current) return;
    didInitReset.current = true;
    reset({
      dishType: aspirationForm.dishType === 100000001 ? '2' : '1',
      ivmTechTime: aspirationForm.ivmTime ?? '',
      gradeABC: aspirationForm.gradeA ?? 0,
      gradeD: aspirationForm.gradeD ?? 0,
      gradeE: aspirationForm.gradeE ?? 0,
      dead: aspirationForm.dead ?? 0,
      total:
        (aspirationForm.gradeA ?? 0) +
        (aspirationForm.gradeD ?? 0) +
        (aspirationForm.gradeE ?? 0),
      numInMat: aspirationForm.ivmMat ?? 0,
      comments: aspirationForm.ivmComment ?? '',
      fertPlan: aspirationForm.fertPlan ?? '',
      usdaSeal: aspirationForm.usdaSeal ?? '',
      ivmCyto: aspirationForm.ivmCyto ?? 100000001,
    });
  }, [aspirationForm, reset]);

  const cancel = () => {
    const sp = new URLSearchParams(params);
    sp.delete('id');
    setParams(sp);
    setShowForm(false);
    refetch();
  };

  const setTimeNow = useCallback(
    () =>
      setValue('ivmTechTime', formatTime(new Date().toISOString()), {
        shouldDirty: false,
        shouldTouch: false,
      }),
    [setValue],
  );

  const onSubmit = async (v: OpuSearchFormValues) => {
    try {
      await saveAspiration({
        input: {
          aspirationId: selectedAspiration?.aspirationId ?? '',
          indexIvmTech: selectedAspiration?.ivmId ?? null,
          siteId: opuSiteId ?? '',
          aspirationDate: opuDate,
          fertPlan: v.fertPlan,
          ivmMat: v.numInMat,
          gradeAbc: v.gradeABC,
          gradeD: v.gradeD,
          gradeE: v.gradeE,
          ivmTechTime: v.ivmTechTime,
          ivmRec: v.total,
          ivmComment: v.comments,
          ivmCyto: v.ivmCyto.toString(),
          usdaSeal: v.usdaSeal,
          enim: v.dead,
          dishType: v.dishType,
          ivmTechId,
        },
        isReturn: 'false',
      });
    } catch {
      return;
    }
    refetch();
  };

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const sub = watch((_, { type }) => {
      if (type !== 'change') return;
      if (!didInitReset.current) return;
      const hasRealChanges = Object.keys(dirtyFields).some(
        (key) => key !== 'ivmTechTime',
      );
      if (!hasRealChanges) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        if (!opuDate || !opuSiteId) return;
        handleSubmit(onSubmit)();
      }, 800);
    });
    return () => {
      sub.unsubscribe();
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [watch, dirtyFields, handleSubmit, opuDate, opuSiteId]);

  return (
    <div className="flex h-[calc(100vh-130px)] max-w-screen-2xl flex-col gap-2">
      <Toast ref={toastRef} />

      <div className="mb-2 flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            icon="pi pi-arrow-left"
            label={t('common.back')}
            onClick={cancel}
          />
          <Button
            icon="pi pi-save"
            label={t('common.save')}
            type="button"
            loading={loading}
            disabled={!opuDate || !opuSiteId || initialLoading}
            onClick={handleSubmit(onSubmit)}
          />
        </div>
        <OpuSearchActions refetch={refetch} poolNumArray={poolNumArray} />
      </div>

      <div className="flex flex-col gap-2 lg:flex-row">
        <OpuSearchFormInfo
          selectedAspiration={selectedAspiration}
          timeZone={tzParam}
          originalTimeZone={originalTimeZone ?? ''}
          aspirationData={
            aspirationData
              ? aspirationData.map((item) => ({
                  ...item,
                  left: item.left ?? 0,
                  right: item.right ?? 0,
                }))
              : []
          }
        />

        <OpuSearchFormFields
          control={control}
          watch={watch}
          setValue={setValue}
          cytoplasmQualityData={cytoplasmQualityData}
          setTimeNow={setTimeNow}
        />
        <OpuSearchFormIvmInfo selectedAspiration={selectedAspiration} />
      </div>
      <OpuSearchFormGrids
        themeMode={themeMode}
        donorScheduleData={donorScheduleData ?? []}
        embFertDataByDonorId={
          embFertDataByDonorId as GET_EMB_FERT_DATA_BY_DONOR_IDQuery
        }
      />
    </div>
  );
};
